/**
 * Environment Configuration Module
 *
 * Provides type-safe access to environment variables with proper defaults
 * and boolean conversion utilities.
 */

/**
 * Parse string boolean from environment variable
 * @param value - String value from env ('true', 'false', undefined)
 * @param defaultValue - Default boolean value if env var is not set
 */
function parseBool(value: string | undefined, defaultValue: boolean): boolean {
  if (value === undefined || value === '') return defaultValue
  return value.toLowerCase() === 'true'
}

/**
 * Application Environment Configuration
 */
export const env = {
  // Environment
  mode: import.meta.env.MODE as 'development' | 'production' | 'test',
  isDev: import.meta.env.DEV,
  isProd: import.meta.env.PROD,

  // Application
  app: {
    env: (import.meta.env.VITE_APP_ENV || 'development') as 'development' | 'staging' | 'production',
    name: import.meta.env.VITE_APP_NAME || 'Die Abtrünnigen',
    url: import.meta.env.VITE_APP_URL || 'http://localhost:3030',
    baseUrl: import.meta.env.BASE_URL || '/',
  },

  // API Configuration
  api: {
    baseUrl: import.meta.env.VITE_API_BASE_URL || '',
  },

  // Feature Flags
  features: {
    performanceMonitoring: parseBool(import.meta.env.VITE_ENABLE_PERFORMANCE_MONITORING, true),
    devLogging: parseBool(import.meta.env.VITE_ENABLE_DEV_LOGGING, import.meta.env.DEV),
    analytics: parseBool(import.meta.env.VITE_ENABLE_ANALYTICS, false),
    firebaseCms: parseBool(import.meta.env.VITE_ENABLE_FIREBASE_CMS, false),
  },

  // Firebase
  firebase: {
    apiKey: import.meta.env.VITE_FIREBASE_API_KEY || '',
    authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || '',
    projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID || '',
    storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET || '',
    messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID || '',
    appId: import.meta.env.VITE_FIREBASE_APP_ID || '',
  },

  // Analytics
  analytics: {
    ga: {
      measurementId: import.meta.env.VITE_GA_MEASUREMENT_ID || '',
    },
    gtm: {
      id: import.meta.env.VITE_GTM_ID || '',
    },
  },

  // Social Media
  social: {
    facebookAppId: import.meta.env.VITE_FACEBOOK_APP_ID || '',
    twitterHandle: import.meta.env.VITE_TWITTER_HANDLE || '',
    ogImageUrl: import.meta.env.VITE_OG_IMAGE_URL || '',
  },

  // Contact
  contact: {
    email: import.meta.env.VITE_CONTACT_EMAIL || '',
    supportEmail: import.meta.env.VITE_SUPPORT_EMAIL || '',
  },

  // External Services
  sentry: {
    dsn: import.meta.env.VITE_SENTRY_DSN || '',
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT || import.meta.env.MODE,
  },

  // Build Configuration
  build: {
    sourcemap: parseBool(import.meta.env.VITE_BUILD_SOURCEMAP, false),
    analyzeBundle: parseBool(import.meta.env.VITE_ANALYZE_BUNDLE, false),
  },

  // Development
  dev: {
    port: parseInt(import.meta.env.VITE_PORT || '3030', 10),
    hmr: parseBool(import.meta.env.VITE_HMR, true),
  },

  // Security
  security: {
    cspDirectives: import.meta.env.VITE_CSP_DIRECTIVES || '',
  },
} as const

/**
 * Type-safe environment configuration
 */
export type EnvConfig = typeof env

/**
 * Validate environment configuration
 * Logs warnings for missing required variables
 */
export function validateEnv(): void {
  const warnings: string[] = []

  // Check for production requirements
  if (env.isProd) {
    if (!env.app.url || env.app.url.includes('localhost')) {
      warnings.push('VITE_APP_URL should be set to production URL')
    }

    if (env.features.analytics && !env.analytics.ga.measurementId && !env.analytics.gtm.id) {
      warnings.push('Analytics enabled but no GA or GTM ID configured')
    }
  }

  // Log warnings
  if (warnings.length > 0 && env.features.devLogging) {
    // eslint-disable-next-line no-console
    console.warn('[Environment Config] Validation warnings:')
    // eslint-disable-next-line no-console
    warnings.forEach(warning => console.warn(`  - ${warning}`))
  }
}

// Auto-validate on import in development
if (env.isDev) {
  validateEnv()
}

export default env
